import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Alert,
  Snackbar,
  Skeleton,
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { authService, User } from '../../services/authService';
import { androidColors } from '../../theme/androidTheme';
import Layout from '../../components/Layout/Layout';
import PageTitle from '../../components/PageTitle/PageTitle';

const Profile: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const userInfo = await authService.getUserInfo();
        if (userInfo) {
          const newUser: User = {
            id: userInfo.id || '',
            firstName: userInfo.firstname || '',
            lastName: userInfo.lastname || '',
            email: userInfo.email || '',
            premium: userInfo.premium || 'no',
            onboard: userInfo.onboard || 'no',
            createdAt: userInfo.createdAt || new Date().toISOString(),
          };
          setUser(newUser);
          setFormData({
            firstName: newUser.firstName,
            lastName: newUser.lastName,
          });
        } else {
          const currentUser = authService.getUser();
          if (currentUser) {
            setUser(currentUser);
            setFormData({
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
            });
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des données utilisateur:', error);
        const currentUser = authService.getUser();
        if (currentUser) {
          setUser(currentUser);
          setFormData({
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      if (!user) return;

      const updatedUser = await authService.updateProfile({
        ...user,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });

      setUser(updatedUser);
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: t('profile.updateSuccess'),
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('profile.updateError'),
        severity: 'error',
      });
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
    });
    setIsEditing(false);
  };

  const userName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : t('common.user');
  const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase();
  const userPlan = user?.premium === 'yes' ? t('common.premium') : t('common.lifetime');

  return (
    <Layout title={t('profile.title')}>
      <PageTitle title={t('profile.title')} />
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* En-tête du profil */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              color: androidColors.textColor,
              fontWeight: 'bold',
              mb: 3,
            }}
          >
            {t('profile.personalInfo')}
          </Typography>

          <Card
            sx={{
              backgroundColor: androidColors.cardBackground,
              border: `1px solid ${androidColors.borderColor}`,
              borderRadius: '12px',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {isLoading ? (
                  <>
                    <Skeleton
                      variant="circular"
                      width={80}
                      height={80}
                      sx={{ bgcolor: `${androidColors.primaryGold}20`, mr: 3 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton
                        variant="text"
                        width="60%"
                        height={32}
                        sx={{ bgcolor: `${androidColors.primaryGold}20`, mb: 1 }}
                      />
                      <Skeleton
                        variant="text"
                        width="40%"
                        height={24}
                        sx={{ bgcolor: `${androidColors.primaryGold}20`, mb: 1 }}
                      />
                      <Skeleton
                        variant="text"
                        width="30%"
                        height={24}
                        sx={{ bgcolor: `${androidColors.primaryGold}20` }}
                      />
                    </Box>
                  </>
                ) : (
                  <>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: androidColors.primaryGold,
                    color: androidColors.darkBackground,
                    fontSize: '32px',
                    fontWeight: 'bold',
                    mr: 3,
                  }}
                >
                  {userInitials}
                </Avatar>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: androidColors.textColor,
                      fontWeight: 'bold',
                      mb: 0.5,
                    }}
                  >
                    {userName}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: androidColors.textSecondary, mb: 0.5 }}
                  >
                    {user?.email}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: androidColors.primaryGold,
                      fontWeight: 'bold',
                    }}
                  >
                    {userPlan}
                  </Typography>
                </Box>
                  </>
                )}
              </Box>

              {isEditing ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                      <TextField
                        fullWidth
                        label={t('profile.firstName')}
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: androidColors.textColor,
                            '& fieldset': {
                              borderColor: androidColors.borderColor,
                            },
                            '&:hover fieldset': {
                              borderColor: androidColors.primaryGold,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: androidColors.primaryGold,
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: androidColors.textSecondary,
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: androidColors.primaryGold,
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                      <TextField
                        fullWidth
                        label={t('profile.lastName')}
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: androidColors.textColor,
                            '& fieldset': {
                              borderColor: androidColors.borderColor,
                            },
                            '&:hover fieldset': {
                              borderColor: androidColors.primaryGold,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: androidColors.primaryGold,
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: androidColors.textSecondary,
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: androidColors.primaryGold,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      sx={{
                        borderColor: androidColors.borderColor,
                        color: androidColors.textColor,
                        '&:hover': {
                          borderColor: androidColors.textColor,
                        },
                      }}
                    >
                      {t('profile.cancel')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      sx={{
                        backgroundColor: androidColors.primaryGold,
                        color: androidColors.darkBackground,
                        '&:hover': {
                          backgroundColor: androidColors.primaryGold,
                          opacity: 0.9,
                        },
                        ml: 2,
                      }}
                    >
                      {t('profile.save')}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsEditing(true)}
                    startIcon={<Person />}
                    sx={{
                      borderColor: androidColors.primaryGold,
                      color: androidColors.primaryGold,
                      '&:hover': {
                        borderColor: androidColors.primaryGold,
                        backgroundColor: `${androidColors.primaryGold}10`,
                      },
                    }}
                  >
                    {t('profile.editProfile')}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Informations supplémentaires */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              color: androidColors.textColor,
              fontWeight: 'bold',
              mb: 3,
            }}
          >
            {t('profile.accountInfo')}
          </Typography>

          <Card
            sx={{
              backgroundColor: androidColors.cardBackground,
              border: `1px solid ${androidColors.borderColor}`,
              borderRadius: '12px',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: androidColors.textSecondary, mb: 0.5 }}
                  >
                    {t('profile.accountType')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: androidColors.textColor, fontWeight: 'bold' }}
                  >
                    {userPlan}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: androidColors.textSecondary, mb: 0.5 }}
                  >
                    {t('profile.signupDate')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: androidColors.textColor }}
                  >
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{
            backgroundColor: snackbar.severity === 'success' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(211, 47, 47, 0.1)',
            color: androidColors.textColor,
            '& .MuiAlert-icon': {
              color: snackbar.severity === 'success' ? '#2E7D32' : '#D32F2F',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default Profile; 